'use client'
import React, { useEffect, useState } from "react";
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Link, Stack, Typography } from "@mui/material";
import { getCookiePreferences, setCookiePreferences, shouldShowCookieConsent } from "./cookies";

const GOOGLE_ANALYTICS_ID = "G-VLNK9WP6LK";
const CLARITY_PROJECT_ID = "xlsinsfde4";

function loadOptionalAnalytics(preferences) {
    if (preferences.analytics && !document.getElementById("google-analytics-script")) {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function gtag() {
            window.dataLayer.push(arguments);
        };
        window.gtag("js", new Date());
        window.gtag("config", GOOGLE_ANALYTICS_ID);

        const googleAnalyticsScript = document.createElement("script");
        googleAnalyticsScript.id = "google-analytics-script";
        googleAnalyticsScript.async = true;
        googleAnalyticsScript.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`;
        document.head.appendChild(googleAnalyticsScript);
    }

    if (preferences.clarity && !document.getElementById("microsoft-clarity-script")) {
        window.clarity = window.clarity || function clarity() {
            (window.clarity.q = window.clarity.q || []).push(arguments);
        };

        const clarityScript = document.createElement("script");
        clarityScript.id = "microsoft-clarity-script";
        clarityScript.async = true;
        clarityScript.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
        document.head.appendChild(clarityScript);
    }
}

export default function CookieConsentBanner() {
    const [showBanner, setShowBanner] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);
    const [preferences, setPreferences] = useState({ analytics: false, clarity: false });

    const handleCookieConsent = async () => {
        if (await shouldShowCookieConsent()) setShowBanner(true);
    }

    const savePreferences = async (nextPreferences) => {
        await setCookiePreferences(nextPreferences);
        loadOptionalAnalytics(nextPreferences);
        setShowBanner(false);
        setShowPreferences(false);
    }

    const handleAcceptAll = () => savePreferences({ analytics: true, clarity: true });
    const handleRejectAll = () => savePreferences({ analytics: false, clarity: false });

    const openPreferences = async () => {
        setPreferences(await getCookiePreferences());
        setShowPreferences(true);
    }

    useEffect(() => {
        handleCookieConsent();
        getCookiePreferences().then((savedPreferences) => {
            loadOptionalAnalytics(savedPreferences);
        });

    }, []);

    if (!showBanner) {
        return null;
    }

    return (
        <>
        <Box sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 24 },
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: 'calc(100% - 32px)', sm: 'min(720px, calc(100% - 48px))' },
            backgroundColor: 'background.paper',
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            boxShadow: '0 16px 48px rgba(15, 23, 42, 0.22)',
            padding: { xs: 2, sm: 2.5 },
            textAlign: 'center',
            zIndex: 1300,
        }}>
            <Typography variant="body1" fontWeight={700}>
                Your privacy matters
            </Typography>
            <Typography variant="body2">
                We use essential cookies to make JobStacky work. You can choose which optional analytics cookies to allow; see our&nbsp;
                <Link href="/privacy-policy" color="inherit">Privacy Policy</Link>
                &nbsp;for details.
            </Typography>
            <br/>
            <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} justifyContent="center">
                <Button color="primary" variant="outlined" onClick={handleRejectAll}>Reject all optional</Button>
                <Button color="primary" variant="outlined" onClick={openPreferences}>Manage preferences</Button>
                <Button color="primary" variant="contained" onClick={handleAcceptAll}>Accept all</Button>
            </Stack>
        </Box>
        <Dialog open={showPreferences} onClose={() => setShowPreferences(false)} fullWidth maxWidth="sm">
            <DialogTitle>Cookie preferences</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Essential cookies are always enabled because they are needed for login, security, and core site functions.
                </Typography>
                <FormControlLabel
                    control={<Checkbox checked={preferences.analytics} onChange={(event) => setPreferences({ ...preferences, analytics: event.target.checked })} />}
                    label="Google Analytics — anonymous traffic and site-performance measurement"
                />
                <FormControlLabel
                    control={<Checkbox checked={preferences.clarity} onChange={(event) => setPreferences({ ...preferences, clarity: event.target.checked })} />}
                    label="Microsoft Clarity — clicks, scrolling, and usability insights"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowPreferences(false)}>Cancel</Button>
                <Button variant="contained" onClick={() => savePreferences(preferences)}>Save choices</Button>
            </DialogActions>
        </Dialog>
        </>
    )
}
